import React from 'react';
import Icon from './Icon';

/**
 * SocialButton Component
 * OAuth provider buttons following official brand guidelines
 * 
 * References:
 * - Microsoft: https://learn.microsoft.com/en-us/entra/identity-platform/howto-add-branding-in-apps
 * - Apple: https://developer.apple.com/design/human-interface-guidelines/sign-in-with-apple
 * - Google: https://developers.google.com/identity/branding-guidelines
 * - LinkedIn: Standard OAuth button styling
 */
export default function SocialButton({
  provider, // 'microsoft' | 'linkedin' | 'apple' | 'google'
  children,
  className = '',
  ...props
}) {
  const providerStyles = {
    microsoft: {
      bg: 'bg-white',
      hover: 'hover:bg-gray-50',
      text: 'text-[#5E5E5E]',
      border: 'border border-[#8C8C8C]',
      icon: 'microsoft',
      iconSize: 20,
    },
    linkedin: {
      bg: 'bg-[#0A66C2]',
      hover: 'hover:bg-[#004182]',
      text: 'text-white',
      icon: 'linkedin',
      iconSize: 20,
    },
    apple: {
      bg: 'bg-black',
      hover: 'hover:bg-gray-900',
      text: 'text-white',
      icon: 'apple',
      iconSize: 18,
    },
    google: {
      bg: 'bg-white',
      hover: 'hover:bg-gray-50',
      text: 'text-[#3C4043]',
      border: 'border border-[#DADCE0]',
      icon: 'google',
      iconSize: 18,
    },
  };

  const style = providerStyles[provider] || providerStyles.microsoft;

  const buttonClasses = [
    'inline-flex items-center justify-center gap-3',
    'w-full h-11 px-6',
    'font-burns font-medium text-[15px]',
    'rounded-md',
    'transition-all duration-150',
    'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-eggs-yellow',
    'shadow-sm',
    style.bg,
    style.hover,
    style.text,
    style.border || '',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    className
  ].join(' ');

  return (
    <button className={buttonClasses} {...props}>
      {style.icon && (
        <Icon name={style.icon} size={style.iconSize} />
      )}
      <span>{children}</span>
    </button>
  );
}

// Convenience exports
export function MicrosoftButton(props) {
  return <SocialButton provider="microsoft" {...props} />;
}

export function LinkedInButton(props) {
  return <SocialButton provider="linkedin" {...props} />;
}

export function AppleButton(props) {
  return <SocialButton provider="apple" {...props} />;
}

export function GoogleButton(props) {
  return <SocialButton provider="google" {...props} />;
}
